import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Play, Download, Video } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { videoService } from '@/services/videos';
import { statisticsService } from '@/services/stats';
import Skeleton from 'react-loading-skeleton';

interface LibraryVideo {
  cart_items: any;
  videos: any;
  video: any;
  id: string;
  court_id: string;
  url: string;
  watch_count: number;
  price: number;
  purchase_count: boolean;
  download_count: number;
  created_at: string;
  updated_at: string;
  // Used for user library
  user_id: string;
  clip_id: string;
  bought: string;
  merchant_name: string;
  merchant_address: string;
  court_name: string;
  court_location: string;
}

const Library = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [videos, setVideos] = useState<LibraryVideo[]>([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getRecentVideos = async () => {
      try {
        const response = await videoService.fetchUsersVideoLibrary();

        const usersVideos = response.cart_items.map((video) => ({
          id: video.id,
          user_id: video.user_id,
          clip_id: video.clip_id,
          bought: video.bought,
          created_at: video.created_at,
          updated_at: video.updated_at,
          url: video.url,
          price: video.price,
          watch_count: video.watch_count,
          purchase_count: video.purchase_count,
          download_count: video.download_count,
          merchant_name: video.merchant_name,
          merchant_address: video.merchant_address,
          court_name: video.court_name,
          court_location: video.court_location,
        }));

        setVideos(usersVideos);
      } catch (error) {
        console.error("Failed to fetch videos:", error);
      } finally {
        setLoading(false);
      }
    };
    getRecentVideos();
  }, []);

  const formatHour = (hour: number) => {
    return `${hour.toString().padStart(2, '0')}:00–${(hour + 1).toString().padStart(2, '0')}:00`;
  };

  const handlePlay = (videoId: number) => {
    navigate(`/videos/${videoId}`);
  };

  const handleDownload = (video: LibraryVideo) => {
    statisticsService.addDownloadCount(video.clip_id);
    if (!video?.url) {
      toast({
        title: "Download failed",
        description: "No video available to download.",
        variant: "destructive",
      });
      return;
    }
    const secureUrl = video.url.replace(/^http:\/\//i, "https://");

    window.open(secureUrl, "_blank", "noopener,noreferrer");

    toast({
      title: "Download started",
      description: `Downloading ${video.court_name} video from ${new Date(video.created_at).toLocaleDateString()}`,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100 pt-12">
      {/* Header */}
      <header className="pt-6 pb-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4 px-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/brands')}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>

          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              My Video Library
            </h1>
            <p className="text-muted-foreground text-sm">
              Welcome back,{" "}
              <span className="font-medium">
                {loading ? <Skeleton width={120} inline /> : user?.name}
              </span>
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-6">
        {loading ? (
          <>
            <div className="mb-6">
              <Skeleton width={220} height={28} />
              <Skeleton width={340} height={16} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="overflow-hidden rounded-2xl border bg-white/80 backdrop-blur shadow-sm">
                  <div className="aspect-video">
                    <Skeleton height="100%" />
                  </div>

                  <CardContent className="p-5 space-y-3">
                    <Skeleton height={20} width="70%" />
                    <Skeleton height={14} width="50%" />

                    <div className="space-y-2 pt-2">
                      <Skeleton height={12} />
                      <Skeleton height={12} />
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Skeleton height={36} className="flex-1" />
                      <Skeleton height={36} width={44} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) :
          videos.length > 0 ? (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Your Videos ({videos.length})</h2>
                <p className="text-muted-foreground">Watch and download your purchased video replays</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map((video) => (
                  <Card
                    key={video.id}
                    className="overflow-hidden rounded-2xl border bg-white/80 backdrop-blur shadow-sm hover:shadow-xl transition-all group"
                  >
                    {/* Video Preview */}
                    <div className="aspect-video relative overflow-hidden">

                      <video
                        src={video.url}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        muted
                        loop
                        autoPlay
                      />

                      {/* Dark Overlay */}
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition" />

                      {/* Play Button Center */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                        <Button
                          size="icon"
                          className="rounded-full w-12 h-12"
                          onClick={() => handlePlay(Number(video.clip_id))}
                        >
                          <Play className="w-5 h-5" />
                        </Button>
                      </div>

                      {/* Purchased Badge */}
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-green-600 text-white shadow">
                          Purchased
                        </Badge>
                      </div>
                    </div>

                    <CardContent className="p-5">
                      <h3 className="font-semibold text-lg mb-1">
                        {video.court_name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {video.merchant_name}
                      </p>

                      <div className="space-y-1 text-xs text-muted-foreground mb-4">
                        <div className="flex justify-between">
                          <span>Date</span>
                          <span className="font-medium">
                            {new Date(video.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Time</span>
                          <span className="font-medium">
                            {formatHour(new Date(video.created_at).getHours())}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          className="flex-1"
                          size="sm"
                          onClick={() => handlePlay(Number(video.clip_id))}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Watch
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(video)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <Video className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-semibold mb-2">No Videos Yet</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                You haven't purchased any video replays yet. Browse courts to find and purchase your game videos.
              </p>
              <Button onClick={() => navigate('/brands')} size="lg">
                Browse Courts
              </Button>
            </div>
          )}

        {/* Quick Stats */}
        {videos.length > 0 && (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="rounded-2xl bg-white/80 backdrop-blur border shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  {loading ? <Skeleton width={40} /> : videos.length}
                </div>
                <div className="text-sm text-muted-foreground">
                  Total Videos
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl bg-white/80 backdrop-blur border shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {loading ? (
                    <Skeleton width={40} />
                  ) : (
                    new Set(videos.map(v => v.merchant_name)).size
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Court Brands
                </div>
              </CardContent>
            </Card>

            {/* <Card className="rounded-2xl bg-white/80 backdrop-blur border shadow-sm">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {Math.round(videos.length * 1.5)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Total Hours
                </div>
              </CardContent>
            </Card> */}
          </div>
        )}
      </main>
    </div>
  );
};

export default Library;